import * as analytics from "@/modules/analytics";
import { valueNames } from "@/modules/Constants";
import type ShopWindowConfig from "@/plugin/ShopWindowConfig";
import type ShopWindowConfigList from "@/plugin/ShopWindowConfigList";
import {
  endDoingStuff,
  setDoingStuffTimeout,
  startDoingStuff,
} from "@/stores/experience/interaction";
import { experienceStore } from "@/stores/experience/store";
import type { ExperienceModel } from "@/stores/experience/types";

interface RegionPayload {
  region: string;
}

interface ShopWindowPayload extends RegionPayload {
  name: string;
}

interface ShopWindowIndexPayload extends ShopWindowPayload {
  index: number;
}

function shopWindowConfig(state: ExperienceModel, name: string) {
  const configs = state.applicationData.getValue(
    valueNames.SHOP_WINDOW_CONFIG_LIST,
  ) as ShopWindowConfigList;
  return configs.atName(name);
}

function trackShopWindowIndex(
  state: ExperienceModel,
  name: string,
  how: string,
  data: RegionPayload,
  config: ShopWindowConfig,
) {
  // Index -1 has no cluster index, so clusterArticleIndexAt throws for it.
  const clusterIndex = config.clusterArticleIndexAt(
    state.experienceData,
    config.index,
  );
  const article =
    clusterIndex && clusterIndex.index > -1
      ? state.experienceData.getArticle(
          clusterIndex.clusterName,
          clusterIndex.cindex,
          clusterIndex.index,
        )
      : null;
  const id = article ? article.id : 0;

  analytics.track(name, {
    pix_how: how,
    pix_offset: state.experienceData.nowTime,
    pix_appregion: data.region,
    pix_id: id,
    pix_cluster: clusterIndex.clusterName,
    pix_cindex: clusterIndex.cindex,
    pix_index: clusterIndex.index,
    pix_sw: config.name,
    pix_sw_index: config.index,
  });
}

function setVideoPositionFromShopWindowIndex(
  state: ExperienceModel,
  config: ShopWindowConfig,
) {
  if (config.data.scrub === true) {
    const timelineData = config.getTimelineData(state.experienceData);
    if (timelineData) {
      state.applicationData.setVideoPosition(timelineData.offset as number);
    }
  }
}

export function incrementShopWindowIndex(region: string, name: string) {
  return experienceStore.dispatch(
    { region: region, name: name },
    (state, data: ShopWindowPayload) => {
      const config = shopWindowConfig(state, data.name);
      if (config && config.incrementShopWindowIndex(state.experienceData)) {
        setDoingStuffTimeout();
        setVideoPositionFromShopWindowIndex(state, config);
        trackShopWindowIndex(state, "Select Item", "increment", data, config);
      }
    },
  );
}

export function decrementShopWindowIndex(region: string, name: string) {
  return experienceStore.dispatch(
    { region: region, name: name },
    (state, data: ShopWindowPayload) => {
      const config = shopWindowConfig(state, data.name);
      if (config && config.decrementShopWindowIndex(state.experienceData)) {
        setDoingStuffTimeout();
        setVideoPositionFromShopWindowIndex(state, config);
        trackShopWindowIndex(state, "Select Item", "decrement", data, config);
      }
    },
  );
}

export function setShopWindowIndex(
  region: string,
  name: string,
  index: number,
) {
  return experienceStore.dispatch(
    { region: region, name: name, index: index },
    (state, data: ShopWindowIndexPayload) => {
      const config = shopWindowConfig(state, data.name);
      if (config) {
        const previousIndex = config.index;
        if (config.setShopWindowIndex(state.experienceData, data.index)) {
          setVideoPositionFromShopWindowIndex(state, config);
          trackShopWindowIndex(
            state,
            "Select Item",
            data.index > previousIndex ? "increment" : "decrement",
            data,
            config,
          );
        }
      }
    },
  );
}

// Interaction start/end only move the time-sync hold; they never notify.

export function userInteractionStart(region: string) {
  return experienceStore.dispatch({ region: region }, () => {
    startDoingStuff();
    return false;
  });
}

export function userInteractionEnd(region: string) {
  return experienceStore.dispatch({ region: region }, () => {
    endDoingStuff();
    return false;
  });
}

export function articleTransitionStart(
  region: string,
  name: string,
  index: number,
) {
  return experienceStore.dispatch(
    { region: region, name: name, index: index },
    () => {
      startDoingStuff();
      return false;
    },
  );
}

export function articleTransitionEnd(
  region: string,
  name: string,
  index: number,
) {
  return experienceStore.dispatch(
    { region: region, name: name, index: index },
    () => {
      endDoingStuff();
      setDoingStuffTimeout();
      return false;
    },
  );
}

export function openShopWindow(region: string) {
  return experienceStore.dispatch(
    { region: region },
    (state, data: RegionPayload) => {
      if (state.applicationData.showShopWindow() === true) {
        return false;
      }

      state.applicationData.setShowShopWindow(true);
      state.template.setShowPanel(true);

      analytics.track("Open Shop Window", {
        pix_offset: state.experienceData.nowTime,
        pix_appregion: data.region,
      });
    },
  );
}

export function closeShopWindow(region: string) {
  return experienceStore.dispatch(
    { region: region },
    (state, data: RegionPayload) => {
      if (state.applicationData.showShopWindow() === false) {
        return false;
      }

      state.applicationData.setShowShopWindow(false);
      state.template.setShowPanel(false);

      analytics.track("Close Shop Window", {
        pix_offset: state.experienceData.nowTime,
        pix_appregion: data.region,
      });
    },
  );
}
